import React from 'react';
import {
    Typography,
    Box,
    Grid
} from '@mui/material';
import { typography, colors } from "./styles/globals.js"
import { Colors } from "./styles/colors"

const styles = {
    container: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        maxWidth: "280px",
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(15px)',
        borderRadius: '16px',
        minHeight: "200px",
        overflow: 'visible'
    },
    contentBox: {
        width: '100%',
        position: 'relative'
    },
    title: {
        color: colors.lightGrey,
        fontSize: typography.fontSizes.small,
        textAlign: 'left'
    },
    titleBox: {
        marginTop: 2,
        marginLeft: 2,
    },
    chartBox: {
        width: '100%',
        height: '150px',
        position: 'relative'
    },
    labelBox: {
        position: 'absolute',
        bottom: '10px',
        left: '50%',
        transform: 'translateX(-50%)',
    },
    label: {
        backgroundColor: '', // Will be set dynamically
        color: 'white',
        padding: '2px 8px',
        borderRadius: '15px',
        fontSize: '0.7rem',
        fontWeight: '600',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
        whiteSpace: 'nowrap',
    },
    labelText: {
        fontSize: typography.fontSizes.small,
        color: 'inherit'
    },
    descriptionBox: {
        width: '100%',
        mt: 2,
        px: 1
    },
    description: {
        color: colors.white,
        lineHeight: 1.4,
        fontSize: typography.fontSizes.small,
        wordWrap: 'break-word',
        overflow: 'visible'
    }
}

const RiskScoreDisplay = ({ score }) => {
    // Ensure score is within bounds
    const validScore = Math.min(100, Math.max(0, score));

    // Determine risk level based on score
    const getRiskLevel = (score) => {
        if (score <= 30) return 'LOW';
        if (score <= 60) return 'MEDIUM';
        return 'HIGH';
    };

    const riskLevel = getRiskLevel(validScore);

    // Configuration for different risk levels
    const riskConfig = {
        LOW: {
            color: Colors.SUCCESS_GREEN,
            label: 'Low risk',
            bgGradient: `linear-gradient(135deg, ${Colors.SYNTHETIC_BLACK} 0%, ${Colors.SYNTHETIC_BLACK} 70%, ${Colors.SUCCESS_GREEN} 100%)`
        },
        MEDIUM: {
            color: Colors.WARNING_ORANGE,
            label: 'Moderate risk',
            bgGradient: `linear-gradient(135deg, ${Colors.SYNTHETIC_BLACK} 0%, ${Colors.SYNTHETIC_BLACK} 70%, ${Colors.WARNING_ORANGE} 100%)`
        },
        HIGH: {
            color: Colors.ERROR_RED,
            label: 'High risk',
            bgGradient: `linear-gradient(135deg, ${Colors.SYNTHETIC_BLACK} 0%, ${Colors.SYNTHETIC_BLACK} 70%, ${Colors.ERROR_RED} 100%)`
        }
    };

    const config = riskConfig[riskLevel];
    const totalSegments = 25;
    const startAngle = -180;
    const endAngle = 10;
    const angleRange = endAngle - startAngle;
    const segmentAngle = angleRange / totalSegments;
    const radius = 110;
    const centerX = 140;
    const centerY = 140;
    const segmentWidth = 10;
    const segmentLength = 20;

    // Calculate number of active segments based on validScore percentage
    const activeSegments = Math.round((validScore / 100) * totalSegments);

    // Generate segments
    const segments = [];
    for (let i = 0; i < totalSegments; i++) {
        const angle = startAngle + (i * segmentAngle);
        const isActive = i < Math.max(1, activeSegments); // Ensure minimum 1 segment
        const color = isActive ? config.color : 'rgba(255, 255, 255, 0.35)';

        const startRadius = radius - segmentLength;
        const endRadius = radius;

        const x1 = centerX + Math.cos((angle * Math.PI) / 180) * startRadius;
        const y1 = centerY + Math.sin((angle * Math.PI) / 180) * startRadius;
        const x2 = centerX + Math.cos((angle * Math.PI) / 180) * endRadius;
        const y2 = centerY + Math.sin((angle * Math.PI) / 180) * endRadius;

        segments.push(
            <line
                key={i}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={color}
                strokeWidth={segmentWidth}
                strokeLinecap="round"
            />
        );
    }

    return (
        <Grid item xs={12}>
            <Box sx={{ ...styles.container }}>
                <Box sx={styles.contentBox}>
                    <Box sx={styles.titleBox}>
                        <Typography sx={styles.title}>
                            Risk Rating
                        </Typography>
                    </Box>

                    <Box sx={styles.chartBox}>
                        <svg width="100%" height="100%" viewBox="0 0 280 150" preserveAspectRatio="xMidYMid meet">
                            {segments}
                        </svg>

                        {/* Risk level label */}
                        <Box sx={styles.labelBox}>
                            <Box sx={{
                                ...styles.label,
                                backgroundColor: config.color,
                            }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M9 17L4 12l-2 2 7 7 14-14-2-2z" />
                                </svg >
                                <Typography sx={styles.labelText}>
                                    {config.label}
                                </Typography>
                            </Box >
                        </Box >
                    </Box >

                    {/* <Box sx={styles.descriptionBox}>
                        <Typography sx={styles.description}>
                            Lorem ipsum dolor sit amet consectetur. Nibh tellus urna ut nunc pharetra. Ultricies
                        </Typography>
                    </Box> */}
                </Box >
            </Box >
        </Grid >
    );
};

export default RiskScoreDisplay;
